import { useReduxState } from 'hooks';
import { MoreOptions } from 'components/common';
import { DropdownLink } from 'components/common/dropdown';
import { CoursesTileCopy, CoursesTileRemoveAdded } from '.';

import type { CourseInView } from './Courses';

interface Props {
  course: CourseInView;
  updateCourses: () => Promise<void>;
}

const CoursesTileDropdown = ({
  course: {
    adminAccess,
    adminPermissions,
    chapters,
    id,
    isAddedUser,
    isAdmin,
    isOwnerOrSuperadmin,
    slug,
    title,
    unreadFeedbackCount,
    unreviewedAnswerCount
  },
  updateCourses
}: Props) => {
  const { messages, user } = useReduxState();

  const unpublishedChapters = chapters.filter(
    (chapter) => !chapter.publishDate
  ).length;

  const editedChapters = chapters.filter((chapter) => chapter.isDraft).length;

  const handleClick = (ev) => {
    ev.stopPropagation();
  };

  return (
    <MoreOptions
      align="right"
      buttonStyle={{ borderRadius: 10 }}
      category="lighter"
      showNotificationIcon={Boolean(
        (isOwnerOrSuperadmin &&
          (unreadFeedbackCount ||
            unreviewedAnswerCount ||
            unpublishedChapters ||
            editedChapters)) ||
          (adminPermissions.includes('view-feedback') && unreadFeedbackCount) ||
          (adminPermissions.includes('review-tasks') &&
            unreviewedAnswerCount) ||
          (adminPermissions.includes('edit-content') && unpublishedChapters) ||
          (adminPermissions.includes('edit-content') && editedChapters)
      )}
      style={{ marginTop: 2 }}
      styleContentButtons={false}
    >
      {isOwnerOrSuperadmin || isAdmin ? (
        <div className="admin-links">
          {isOwnerOrSuperadmin ? (
            <DropdownLink
              href={`/admin/courses/${slug}`}
              icon="home"
              onClick={handleClick}
              text={messages.commonSummary}
            />
          ) : null}

          {isOwnerOrSuperadmin || adminPermissions.includes('edit-content') ? (
            <DropdownLink
              href={`/admin/courses/${slug}/edit`}
              icon="pen"
              onClick={handleClick}
              text={messages.pagesEdit}
              unreadItems={unpublishedChapters}
              warningItems={editedChapters}
            />
          ) : null}

          {isOwnerOrSuperadmin || adminPermissions.includes('edit-settings') ? (
            <DropdownLink
              href={`/admin/courses/${slug}/settings`}
              icon="cog"
              onClick={handleClick}
              text={messages.pagesSettings}
            />
          ) : null}

          {isOwnerOrSuperadmin ? (
            <DropdownLink
              href={`/admin/courses/${slug}/publish`}
              icon="check-circle"
              onClick={handleClick}
              text={messages.commonPublish}
            />
          ) : null}

          {isOwnerOrSuperadmin || adminPermissions.includes('manage-users') ? (
            <DropdownLink
              href={`/admin/courses/${slug}/share`}
              icon="user"
              onClick={handleClick}
              text={messages.commonShare}
            />
          ) : null}

          {isOwnerOrSuperadmin ||
          (adminPermissions.includes('review-tasks') &&
            adminPermissions.includes('view-feedback')) ? (
            <DropdownLink
              href={`/admin/courses/${slug}/follow/users`}
              icon="chart-line"
              onClick={handleClick}
              text={messages.pagesFollow}
              unreadItems={
                unreviewedAnswerCount || unreadFeedbackCount
                  ? unreviewedAnswerCount + unreadFeedbackCount
                  : null
              }
            />
          ) : adminPermissions.includes('review-tasks') ? (
            <DropdownLink
              href={`/admin/courses/${slug}/follow/users`}
              icon="chart-line"
              onClick={handleClick}
              text={messages.pagesFollow}
              unreadItems={unreviewedAnswerCount}
            />
          ) : adminPermissions.includes('view-feedback') ? (
            <DropdownLink
              href={`/admin/courses/${slug}/follow/feedback`}
              icon="chart-line"
              onClick={handleClick}
              text={messages.pagesFollow}
              unreadItems={unreadFeedbackCount}
            />
          ) : adminPermissions.includes('view-analytics') ? (
            <DropdownLink
              href={`/admin/courses/${slug}/follow/visitors`}
              icon="chart-line"
              onClick={handleClick}
              text={messages.pagesFollow}
            />
          ) : null}

          {isOwnerOrSuperadmin ? (
            <DropdownLink
              href={`/admin/courses/${slug}/manage`}
              icon="tools"
              onClick={handleClick}
              text={messages.pagesManage}
            />
          ) : null}
        </div>
      ) : adminAccess === 'copyable' &&
        user?.roles?.includes('admin') &&
        user?.permissions?.includes('create-courses') ? (
        <>
          <CoursesTileCopy
            courseId={id}
            courseTitle={title}
            isDropdownButton={true}
          />
          {isAddedUser ? (
            <CoursesTileRemoveAdded
              courseId={id}
              updateCourses={updateCourses}
            />
          ) : null}
        </>
      ) : (
        <CoursesTileRemoveAdded courseId={id} updateCourses={updateCourses} />
      )}
    </MoreOptions>
  );
};

export default CoursesTileDropdown;
